/**
 * Manual Stop Validation
 *
 * Zod schemas for staging manual stops (business or customer) from the recall map.
 *
 * Validation, not encoding: values are normalized (NFC, control characters stripped,
 * whitespace collapsed) and checked against strict shapes, then stored as plain text.
 * Escaping happens where text is rendered (React escapes automatically; print HTML uses
 * esc() in printRoutes.ts). The server re-validates everything in stage_manual_stops_v1.
 */

import { z } from 'zod';
import { parsePhoneNumberFromString } from 'libphonenumber-js/min';

// =============================================================================
// Preset Reasons
// =============================================================================

/**
 * Preset reasons for manual stops — the single source of truth for reason values.
 * The same values are enforced by stage_manual_stops_v1 (vehixdb-prod) and labelled in
 * vehix-app; add new values in all three places.
 *
 * `kinds` says which stop types offer the reason. Values are never relabelled per stop
 * type: a recall at a customer's home is its own value (customer_recall), so reports,
 * printouts and the app read the same thing.
 */
export const MANUAL_STOP_REASONS = [
  { value: 'oil_change', label: 'Oil Change', kinds: ['business', 'customer'] },
  { value: 'auction_recall', label: 'Auction Recall', kinds: ['business'] },
  { value: 'dealer_recall', label: 'Dealer Recall', kinds: ['business'] },
  { value: 'customer_recall', label: 'Recall', kinds: ['customer'] },
  { value: 'other', label: 'Other', kinds: ['business', 'customer'] }
] as const;

export type ManualStopReasonValue = (typeof MANUAL_STOP_REASONS)[number]['value'];

const REASON_VALUES = MANUAL_STOP_REASONS.map(r => r.value) as [
  ManualStopReasonValue,
  ...ManualStopReasonValue[]
];

/** Reasons that carry recall campaigns (shows the campaign picker, counts as recall work). */
const RECALL_REASON_VALUES: readonly string[] = [
  'auction_recall',
  'dealer_recall',
  'customer_recall'
];

export function isRecallReason(value: string | null | undefined): boolean {
  return !!value && RECALL_REASON_VALUES.includes(value);
}

/** Whether a reason may be used on a stop type. */
export function isReasonAllowedFor(value: string, stopKind: 'business' | 'customer'): boolean {
  const reason = MANUAL_STOP_REASONS.find(r => r.value === value);
  return !!reason && (reason.kinds as readonly string[]).includes(stopKind);
}

/** Reason options offered for a stop type, in display order. */
export function getManualStopReasonsFor(stopKind: 'business' | 'customer') {
  return MANUAL_STOP_REASONS.filter(r => (r.kinds as readonly string[]).includes(stopKind));
}

/**
 * Get human-readable label for a manual stop reason value.
 * @param value - The snake_case reason value (e.g., 'oil_change')
 * @returns The formatted label (e.g., 'Oil Change'), or the original value if not found
 *   (custom "Other" reasons are stored as their free text)
 */
export function getManualStopReasonLabel(value: string): string {
  const found = MANUAL_STOP_REASONS.find(r => r.value === value);
  return found?.label || value;
}

// =============================================================================
// Limits (mirrored by DB constraints in vehixdb-prod)
// =============================================================================

export const MANUAL_STOP_LIMITS = {
  personName: 80,
  locationName: 200,
  address: 200,
  addressLine2: 50,
  city: 100,
  email: 254,
  note: 1000,
  customReason: 255,
  vehicleText: 50,
  campaignNumber: 40,
  recallTitle: 500,
  vehicles: 20,
  campaignsPerVehicle: 20
} as const;

// =============================================================================
// Text normalization & sensitive-data detection
// =============================================================================

/**
 * Normalize free text: NFC, strip control characters (keeping newlines when
 * `multiline`), turn tabs into spaces, collapse repeated spaces, trim.
 */
export function cleanText(input: string, multiline = false): string {
  const normalized = input.normalize('NFC').replace(/\t/g, ' ');
  // eslint-disable-next-line no-control-regex -- intentionally matching control characters
  const controlChars = multiline ? /[\u0000-\u0009\u000B-\u001F\u007F]/g : /[\u0000-\u001F\u007F]/g;
  const stripped = normalized.replace(/\r\n?/g, '\n').replace(controlChars, '');
  const collapsed = stripped.replace(/\u00A0/g, ' ').replace(/ {2,}/g, ' ');
  if (!multiline) return collapsed.trim();
  const lines = collapsed.split('\n').map(line => line.trim());
  return lines
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function passesLuhn(digits: string): boolean {
  let sum = 0;
  let double = false;
  for (let i = digits.length - 1; i >= 0; i--) {
    let d = Number(digits[i]);
    if (double) {
      d *= 2;
      if (d > 9) d -= 9;
    }
    sum += d;
    double = !double;
  }
  return sum % 10 === 0;
}

/**
 * True when text contains what looks like a payment card number (13–19 digits,
 * spaces/dashes allowed, Luhn-valid) or a formatted SSN (123-45-6789 / 123 45 6789).
 * Notes are shown to technicians and printed — this data must never be typed there.
 */
export function hasSensitiveNumber(text: string): boolean {
  if (/\b\d{3}[- ]\d{2}[- ]\d{4}\b/.test(text)) return true;
  const runs = text.match(/\d(?:[ -]?\d){12,18}/g) ?? [];
  return runs.some(run => passesLuhn(run.replace(/\D/g, '')));
}

const SENSITIVE_NUMBER_MESSAGE =
  "Don't include card numbers or Social Security numbers. Technicians see this text.";

// =============================================================================
// VIN
// =============================================================================

const VIN_TRANSLITERATION: Record<string, number> = {
  A: 1, B: 2, C: 3, D: 4, E: 5, F: 6, G: 7, H: 8,
  J: 1, K: 2, L: 3, M: 4, N: 5, P: 7, R: 9,
  S: 2, T: 3, U: 4, V: 5, W: 6, X: 7, Y: 8, Z: 9
}; // prettier-ignore
const VIN_WEIGHTS = [8, 7, 6, 5, 4, 3, 2, 10, 0, 9, 8, 7, 6, 5, 4, 3, 2];

/** ISO 3779 / 49 CFR 565 check digit (position 9). Required on every US-market VIN since 1981. */
export function hasValidVinCheckDigit(vin: string): boolean {
  const v = vin.toUpperCase();
  if (!/^[A-HJ-NPR-Z0-9]{17}$/.test(v)) return false;
  let sum = 0;
  for (let i = 0; i < 17; i++) {
    const ch = v.charAt(i);
    const value = /\d/.test(ch) ? Number(ch) : (VIN_TRANSLITERATION[ch] ?? 0);
    sum += value * (VIN_WEIGHTS[i] ?? 0);
  }
  const remainder = sum % 11;
  const expected = remainder === 10 ? 'X' : String(remainder);
  return v.charAt(8) === expected;
}

export const vinSchema = z
  .string()
  .transform(v => v.trim().toUpperCase())
  .refine(v => /^[A-HJ-NPR-Z0-9]{17}$/.test(v), {
    message: 'VIN must be 17 characters (letters I, O and Q are not used)'
  })
  .refine(hasValidVinCheckDigit, { message: "This VIN doesn't pass the check-digit test" });

// =============================================================================
// Field schemas
// =============================================================================

const text = (max: number, opts: { min?: number; label: string; multiline?: boolean }) =>
  z
    .string()
    .transform(v => cleanText(v, opts.multiline))
    .refine(v => v.length >= (opts.min ?? 1), {
      message: `${opts.label} is required`
    })
    .refine(v => v.length <= max, { message: `${opts.label} must be ${max} characters or fewer` });

const optionalText = (max: number, label: string, multiline = false) =>
  z
    .string()
    .optional()
    .transform(v => (v === undefined ? undefined : cleanText(v, multiline) || undefined))
    .refine(v => v === undefined || v.length <= max, {
      message: `${label} must be ${max} characters or fewer`
    });

/** Unicode letters and marks, spaces, apostrophes, periods and hyphens. */
export const personNameSchema = (label: string) =>
  text(MANUAL_STOP_LIMITS.personName, { label }).refine(
    v => /^[\p{L}\p{M}][\p{L}\p{M}' .-]*$/u.test(v),
    { message: `${label} can only contain letters, spaces, apostrophes, periods and hyphens` }
  );

/** US phone number → E.164 (+1XXXXXXXXXX). */
export const usPhoneSchema = z.string().transform((v, ctx) => {
  const parsed = parsePhoneNumberFromString(v.trim(), 'US');
  if (!parsed || !parsed.isValid() || parsed.countryCallingCode !== '1') {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Enter a valid US phone number' });
    return z.NEVER;
  }
  return parsed.number as string;
});

export const optionalEmailSchema = z
  .string()
  .optional()
  .transform(v => (v === undefined ? undefined : v.trim().toLowerCase() || undefined))
  .refine(v => v === undefined || v.length <= MANUAL_STOP_LIMITS.email, {
    message: 'Email is too long'
  })
  .refine(v => v === undefined || z.string().email().safeParse(v).success, {
    message: 'Enter a valid email address'
  });

export const PREFERRED_CONTACT_METHODS = ['call', 'text', 'email'] as const;
export type PreferredContactMethod = (typeof PREFERRED_CONTACT_METHODS)[number];

export const dispatcherNoteSchema = optionalText(MANUAL_STOP_LIMITS.note, 'Note', true).refine(
  v => v === undefined || !hasSensitiveNumber(v),
  { message: SENSITIVE_NUMBER_MESSAGE }
);

export const addressLine2Schema = optionalText(
  MANUAL_STOP_LIMITS.addressLine2,
  'Apt / unit'
).refine(v => v === undefined || !/[<>]/.test(v), { message: 'Apt / unit has invalid characters' });

// =============================================================================
// Address validation summary (from the address-validate edge function)
// =============================================================================

export const ADDRESS_VALIDATION_STATUSES = [
  'confirmed',
  'corrected',
  'unit_missing',
  'unconfirmed',
  'unavailable'
] as const;
export type AddressValidationStatus = (typeof ADDRESS_VALIDATION_STATUSES)[number];

export const addressValidationSummarySchema = z.object({
  provider: z.literal('google_av'),
  status: z.enum(ADDRESS_VALIDATION_STATUSES),
  dpv: z.enum(['Y', 'S', 'D', 'N']).nullable(),
  residential: z.boolean().nullable(),
  business: z.boolean().nullable(),
  validated_at: z.string().datetime(),
  response_id: z.string().max(200).nullable()
});
export type AddressValidationSummary = z.infer<typeof addressValidationSummarySchema>;

// =============================================================================
// Location, contact, vehicles
// =============================================================================

const coordinate = (min: number, max: number) => z.number().finite().min(min).max(max);

const locationBaseSchema = z.object({
  address: text(MANUAL_STOP_LIMITS.address, { min: 5, label: 'Address' }),
  address_line2: addressLine2Schema,
  city: text(MANUAL_STOP_LIMITS.city, { label: 'City' }),
  region: z
    .string()
    .transform(v => v.trim().toUpperCase())
    .refine(v => /^[A-Z]{2}$/.test(v), { message: 'State must be a 2-letter code' }),
  // Required by stage_manual_stops_v1 (and by USPS validation).
  postal_code: z
    .string({ required_error: 'Pick an address that includes a ZIP code' })
    .transform(v => v.trim())
    .refine(v => v.length > 0, { message: 'Pick an address that includes a ZIP code' })
    .refine(v => v.length === 0 || /^\d{5}(-\d{4})?$/.test(v), {
      message: 'ZIP code must be 5 digits (or ZIP+4)'
    }),
  lat: coordinate(-90, 90),
  lng: coordinate(-180, 180)
});

const businessLocationSchema = locationBaseSchema.extend({
  name: text(MANUAL_STOP_LIMITS.locationName, { min: 2, label: 'Location name' }),
  phone: z
    .string()
    .optional()
    .transform(v => v?.trim() || undefined)
    .pipe(z.union([z.undefined(), usPhoneSchema]))
});

const customerContactShape = z.object({
  first_name: personNameSchema('First name'),
  last_name: personNameSchema('Last name'),
  phone_e164: usPhoneSchema,
  email: optionalEmailSchema,
  preferred_contact: z.enum(PREFERRED_CONTACT_METHODS)
});

/** Per-field validators (used for check-on-blur in the form). */
export const customerContactFieldSchemas = customerContactShape.shape;

export const customerContactSchema = customerContactShape.superRefine((c, ctx) => {
  if (c.preferred_contact === 'email' && !c.email) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['email'],
      message: 'Add an email address, or pick another way to contact them'
    });
  }
});
export type CustomerContactInput = z.input<typeof customerContactSchema>;
export type CustomerContact = z.output<typeof customerContactSchema>;

const campaignSchema = z.object({
  campaign_number: text(MANUAL_STOP_LIMITS.campaignNumber, { label: 'Campaign number' }),
  recall_title: optionalText(MANUAL_STOP_LIMITS.recallTitle, 'Recall title')
});

const maxModelYear = () => new Date().getFullYear() + 2;

export const manualStopVehicleSchema = z
  .object({
    vin: vinSchema,
    vehicle_year: z
      .number()
      .int()
      .min(1980)
      .refine(y => y <= maxModelYear(), { message: 'Model year is too far in the future' })
      .optional(),
    vehicle_make: optionalText(MANUAL_STOP_LIMITS.vehicleText, 'Make'),
    vehicle_model: optionalText(MANUAL_STOP_LIMITS.vehicleText, 'Model'),
    manual_stop_reason: z.enum(REASON_VALUES),
    custom_reason: optionalText(MANUAL_STOP_LIMITS.customReason, 'Custom reason').refine(
      v => v === undefined || !hasSensitiveNumber(v),
      { message: SENSITIVE_NUMBER_MESSAGE }
    ),
    campaigns: z.array(campaignSchema).max(MANUAL_STOP_LIMITS.campaignsPerVehicle).default([])
  })
  .superRefine((v, ctx) => {
    if (v.manual_stop_reason === 'other' && !v.custom_reason) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['custom_reason'],
        message: 'Describe the reason'
      });
    }
    if (v.manual_stop_reason !== 'other' && v.custom_reason) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['custom_reason'],
        message: 'A custom reason is only used with "Other"'
      });
    }
    if (!isRecallReason(v.manual_stop_reason) && v.campaigns.length > 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['campaigns'],
        message: 'Campaigns can only be attached to a recall'
      });
    }
  });
export type ManualStopVehicle = z.output<typeof manualStopVehicleSchema>;

// =============================================================================
// Full submission (one location + contact + 1..N vehicles)
// =============================================================================

const submissionShared = {
  address_validation: addressValidationSummarySchema.optional(),
  dispatcher_note: dispatcherNoteSchema,
  vehicles: z
    .array(manualStopVehicleSchema)
    .min(1, 'Add at least one vehicle')
    .max(MANUAL_STOP_LIMITS.vehicles, `Up to ${MANUAL_STOP_LIMITS.vehicles} vehicles per stop`)
};

export const manualStopSubmissionSchema = z
  .discriminatedUnion('stop_kind', [
    z.object({
      stop_kind: z.literal('business'),
      location: businessLocationSchema,
      ...submissionShared
    }),
    z.object({
      stop_kind: z.literal('customer'),
      location: locationBaseSchema,
      contact: customerContactSchema,
      ...submissionShared
    })
  ])
  .superRefine((v, ctx) => {
    if (v.location.lat === 0 && v.location.lng === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['location', 'address'],
        message: 'Pick the address from the suggestions so it can be placed on the map'
      });
    }
    // Customer stops must have been through the USPS check. Its outcome is advisory:
    // unverified, unit-missing and unavailable results are shown to the dispatcher but
    // never block saving — they confirm the address on their end.
    if (v.stop_kind === 'customer' && !v.address_validation) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['location', 'address'],
        message: 'Wait for the address check to finish'
      });
    }
    v.vehicles.forEach((vehicle, i) => {
      if (!isReasonAllowedFor(vehicle.manual_stop_reason, v.stop_kind)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['vehicles', i, 'manual_stop_reason'],
          message: `This reason isn't available for ${v.stop_kind} stops`
        });
      }
    });
  });

export type ManualStopSubmissionInput = z.input<typeof manualStopSubmissionSchema>;
export type ManualStopSubmission = z.output<typeof manualStopSubmissionSchema>;
export type StopKind = ManualStopSubmission['stop_kind'];

/**
 * Flatten zod issues to `{ "location.address": "message" }` (first message per path)
 * for showing errors next to form fields.
 */
export function toFieldErrors(error: z.ZodError): Record<string, string> {
  const out: Record<string, string> = {};
  for (const issue of error.issues) {
    const key = issue.path.join('.');
    if (!(key in out)) out[key] = issue.message;
  }
  return out;
}
